// Button.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { hexToRgba } from 'utilities/Colors';

// CSS
import './Button.css';

// Icons
import Icon from 'svg/Icon';

/**
 * Button
 *
 * Renders a versatile button with optional icon and label content.
 *
 * @param {ReactNode} icon - The icon component to render inside the button.
 * @param {string} iconKey - The icon key to render via Icon (overrides icon).
 * @param {string} iconResource - Optional image URL/import; overrides iconKey and icon.
 * @param {string} iconColor - The color of the icon (default is white).
 * @param {string|number} iconSize - The size of the icon (default is 20).
 * @param {string} endIconKey - Optional trailing icon key (e.g. menu chevron).
 * @param {string} endIconColor - Color for the trailing icon (defaults like iconColor).
 * @param {string|number} endIconSize - Size for the trailing icon (defaults to iconSize).
 * @param {string} label - The label to display on the button.
 * @param {string} labelColor - The color of the label (default is white).
 * @param {string|number} fontSize - The font size of the label (default is 14px).
 * @param {string} fontStyle - The font style of the label (default is normal).
 * @param {string|number} width - The width of the button (default is auto).
 * @param {boolean} enabled - Whether the button is enabled (default is true).
 * @param {Function} onClick - Called when the button is clicked.
 * @param {string} backgroundColor - Optional background color override.
 * @param {string} buttonColor - Optional background color alias.
 * @param {string} foregroundColor - Optional foreground color override.
 * @param {string} borderColor - Optional border color override.
 * @param {string|number} borderRadius - Border radius value (default is 8px).
 * @param {string|number} height - The height of the button (default is 50px).
 * @param {string|number} flex - Optional flex value for layout.
 * @param {string} hoverBackgroundColor - Optional hover background override.
 * @param {string} hoverForegroundColor - Optional hover foreground override.
 * @param {string|number} padding - Optional padding override.
 * @param {string} addon - Optional visual addon for shared themed surfaces.
 *
 * @returns {ReactNode} The rendered component.
 */
const Button = React.memo(({
    icon: IconComponent,
    iconKey,
    iconResource,
    iconColor,
    iconSize = '16px',
    endIconKey,
    endIconColor,
    endIconSize,
    label,
    labelColor,
    title,
    fontSize = '12px',
    fontStyle = 'normal',
    borderRadius = '10px',
    height = '36px',
    width = 'auto',
    enabled = true,
    onClick,
    flex,
    gap = '6px',
    backgroundColor,
    buttonColor,
    foregroundColor,
    borderColor,
    hoverBackgroundColor,
    hoverForegroundColor,
    padding,
    addon = 'default',
}) => {

    // ----- CONTEXT ----- //

    // Theme Context

    const { theme } = useTheme();

    // ----- STATE ----- //

    // Hover State

    const [hovered, setHovered] = useState(false);

    // Indicates whether the button renders any icon content.
    const hasIcon = Boolean(iconResource || iconKey || IconComponent);

    const hasEndIcon = Boolean(endIconKey);

    const resolvedEndIconSize = endIconSize ?? iconSize;

    // Indicates whether the button renders text label content.
    const hasLabel = Boolean(label);

    // Indicates icon-only button mode.
    const iconOnly = hasIcon && !hasLabel && !hasEndIcon;

    // ----- MEMOS ----- //

    // Resolves default padding for icon-only, icon+label, and label-only addons.

    const resolvedPadding = useMemo(() => {
        if (padding !== undefined) return padding;
        if (iconOnly) return '10px 20px';
        if (hasIcon && hasEndIcon) return '10px 10px 10px 10px';
        if (hasIcon) return '10px 18px 10px 10px';
        if (hasEndIcon && hasLabel) return '10px 10px 10px 18px';
        return '10px 18px';
    }, [padding, iconOnly, hasIcon, hasEndIcon, hasLabel]);

    const isGlassAddon = addon === 'glass';

    const isPrimaryChrome = !backgroundColor
        && !buttonColor
        && !isGlassAddon
        && theme.buttonBackgroundColor === theme.highlight;

    const baseBackground = backgroundColor || buttonColor || (isGlassAddon ? 'var(--glassSoft)' : theme.buttonBackgroundColor || theme.backgroundMuted);

    // Resolves current background color based on enabled and hover state.
    const resolvedBackground = useMemo(() => {
        if (!enabled || !hovered) return baseBackground;
        if (hoverBackgroundColor) return hoverBackgroundColor;
        if (backgroundColor || buttonColor) return baseBackground;
        if (isGlassAddon) return 'var(--glass)';
        if (isPrimaryChrome) return theme.highlightHover || theme.buttonHoverBackgroundColor || theme.backgroundHover;
        return theme.buttonHoverBackgroundColor || theme.backgroundHover;
    }, [enabled, hovered, baseBackground, hoverBackgroundColor, backgroundColor, buttonColor, isGlassAddon, isPrimaryChrome, theme.highlightHover, theme.buttonHoverBackgroundColor, theme.backgroundHover]);

    const resolvedBorder = borderColor ?? resolvedBackground;

    const baseIconColor = iconColor || foregroundColor || theme.buttonIconColor || theme.foreground;
    const hoverIconColor = hoverForegroundColor
        || (isPrimaryChrome ? baseIconColor : theme.buttonHoverIconColor)
        || theme.foreground;

    const baseEndIconColor = endIconColor || baseIconColor;
    const hoverEndIconColor = endIconColor
        ? (hoverForegroundColor || baseEndIconColor)
        : hoverIconColor;

    const baseLabelColor = labelColor || foregroundColor || theme.buttonLabelColor || theme.foreground;
    const hoverLabelColor = hoverForegroundColor
        || (isPrimaryChrome ? baseLabelColor : theme.buttonHoverLabelColor)
        || theme.foreground;

    const primaryGlow = theme.highlightGlow || hexToRgba(theme.highlight, 0.28);
    const showPrimaryGlow = isPrimaryChrome && Boolean(theme.highlightGlow);

    // ----- HANDLERS ----- //

    /**
     * Handles click interaction when the button is enabled.
     *
     * @param {MouseEvent} event - Native click event.
     */

    const handleClick = (event) => {
        if (!enabled || !onClick) return;
        onClick(event);
    };

    /**
     * Handles hover entry state.
     */
    const handleMouseEnter = () => {
        setHovered(true);
    };

    /**
     * Handles hover exit state.
     */
    const handleMouseLeave = () => {
        setHovered(false);
    };

    // ----- RENDER ----- //

    return (

        <div
          className={`button-container${isGlassAddon ? ' button-addon-glass' : ''}${showPrimaryGlow ? ' button-container--primary' : ''}`}
          style={{
                ...(showPrimaryGlow ? { '--button-highlight-glow': primaryGlow } : {}),
                backgroundColor: resolvedBackground,
                borderWidth: 1,
                borderColor: resolvedBorder,
                borderRadius,
                boxShadow: isGlassAddon ? '0 12px 34px var(--elevationShadowSoft)' : undefined,
                cursor: enabled ? 'pointer' : 'not-allowed',
                flex,
                gap,
                height,
                //justifyContent: iconOnly ? 'center' : hasIcon ? 'center' : 'center',
                opacity: enabled ? 1 : 0.4,
                padding: resolvedPadding,
                transform: hovered && enabled ? 'translateY(-1px)' : undefined,
                width,
            }}
          title={title}
          onClick={handleClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >

            {/* ICON */}
            {iconResource ? (
                <div className="button-icon">
                    <img
                      alt=""
                      className="button-icon-resource"
                      draggable={false}
                      src={iconResource}
                      style={{
                        height: iconSize,
                        width: iconSize,
                      }}
                    />
                </div>
            ) : iconKey ? (
                <div className="button-icon">
                    <Icon
                      color={hovered ? hoverIconColor : baseIconColor}
                      height={iconSize}
                      iconKey={iconKey}
                      width={iconSize}
                    />
                </div>
            ) : IconComponent ? (

                <div className="button-icon">
                    <IconComponent
                      color={hovered ? hoverIconColor : baseIconColor}
                      height={iconSize}
                      width={iconSize}
                    />
                </div>
            ) : null}

            {/* LABEL */}
            {label && (
                <div
                  className="button-label"
                  style={{
                        color: hovered ? hoverLabelColor : baseLabelColor,
                        fontSize,
                        fontStyle,
                    }}
                >
                    {label}
                </div>
            )}

            {endIconKey ? (
                <div className="button-icon button-icon-end">
                    <Icon
                      color={hovered ? hoverEndIconColor : baseEndIconColor}
                      height={resolvedEndIconSize}
                      iconKey={endIconKey}
                      width={resolvedEndIconSize}
                    />
                </div>
            ) : null}
        </div>
    );
});

// Dev Tools
Button.displayName = 'Button';

export default Button;
